import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { RequireAuth } from "@/components/Auth/RequireAuth";
import { RequireAdmin } from "@/components/Auth/RequireAdmin";
import { RequireSuperAdmin } from "@/components/Auth/RequireSuperAdmin";
import { RequireOrganizer } from "@/components/Auth/RequireOrganizer";
import { PostLoginRedirect } from "@/components/Auth/PostLoginRedirect";
import { AppLayout } from "./components/Layout/AppLayout";
import { Dashboard } from "./pages/Dashboard";
import { EventsList } from "./pages/Events/EventsList";
import { EventGuestManagement } from "./pages/Events/EventGuestManagement";
import { EventCalendar } from "./pages/Events/EventCalendar";
import { EventSchedule } from "./pages/Events/EventSchedule";
import { EventProviders } from "./pages/Events/EventProviders";
import { EventReception } from "./pages/Events/EventReception";
import { EventSeatingPrint } from "./pages/Events/EventSeatingPrint";
import { EventAccessPage } from "./pages/Events/EventAccess";
import { ProvidersList } from "./pages/Providers/ProvidersList";
import { Account } from "./pages/Account";
import { AdminDashboard } from "./pages/Admin/AdminDashboard";
import { SuperAdminLayout } from "./pages/SuperAdmin/SuperAdminLayout";
import { SuperAdminDashboard } from "./pages/SuperAdmin/SuperAdminDashboard";
import { AuditLog } from "./pages/SuperAdmin/AuditLog";
import { PlansAdmin } from "./pages/SuperAdmin/PlansAdmin";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Terms from "./pages/Terms";
import PrivacyNotice from "./pages/PrivacyNotice";

const queryClient = new QueryClient();

// Component to handle legacy event route redirects
const EventRedirect: React.FC<{ subRoute: string }> = ({ subRoute }) => {
  const { eventId } = useParams();
  return <Navigate to={`/app/events/${eventId}/${subRoute}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/acerca-de" element={<About />} />
            <Route path="/precios" element={<Pricing />} />
            <Route path="/terminos" element={<Terms />} />
            <Route path="/aviso-de-privacidad" element={<PrivacyNotice />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* App routes - protected. /app itself routes via PostLoginRedirect
                so guests land on their assigned event and organizers land on
                /app/dashboard. */}
            <Route path="/app" element={
              <RequireAuth>
                <PostLoginRedirect />
              </RequireAuth>
            } />
            <Route path="/app/*" element={
              <RequireAuth>
                <Routes>
                  <Route path="" element={<AppLayout />}>
                    <Route path="dashboard" element={
                      <RequireOrganizer><Dashboard /></RequireOrganizer>
                    } />
                    <Route path="events" element={
                      <RequireOrganizer><EventsList /></RequireOrganizer>
                    } />
                    <Route path="events/:eventId/guests" element={<EventGuestManagement />} />
                    <Route path="events/:eventId/calendar" element={<EventCalendar />} />
                    <Route path="events/:eventId/schedule" element={<EventSchedule />} />
                    <Route path="events/:eventId/providers" element={<EventProviders />} />
                    <Route path="events/:eventId/recepcion" element={<EventReception />} />
                    <Route path="events/:eventId/mesas/imprimir" element={<EventSeatingPrint />} />
                    <Route path="events/:eventId/accesos" element={
                      <RequireOrganizer><EventAccessPage /></RequireOrganizer>
                    } />
                    <Route path="providers" element={
                      <RequireOrganizer><ProvidersList /></RequireOrganizer>
                    } />
                    <Route path="account" element={
                      <RequireOrganizer><Account /></RequireOrganizer>
                    } />
                    {/* Admin route - protected with RequireAdmin */}
                    <Route path="admin" element={
                      <RequireAdmin>
                        <AdminDashboard />
                      </RequireAdmin>
                    } />
                    <Route path="settings" element={
                      <RequireOrganizer>
                        <div className="p-6">Configuración - Próximamente</div>
                      </RequireOrganizer>
                    } />
                  </Route>
                  {/*
                    Super admin tree (Wave 2A) — rendered as a sibling of the
                    AppLayout route because SuperAdminLayout ships its own
                    shell (sidebar + topbar + red "Modo super admin" pill).
                    Plans / Users / Impersonate land in Waves 2A.2 + 2B.
                  */}
                  <Route path="superadmin" element={
                    <RequireSuperAdmin>
                      <SuperAdminLayout />
                    </RequireSuperAdmin>
                  }>
                    <Route index element={<SuperAdminDashboard />} />
                    <Route path="audit" element={<AuditLog />} />
                    <Route path="plans" element={<PlansAdmin />} />
                  </Route>
                </Routes>
              </RequireAuth>
            } />

            {/* Legacy routes redirect */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/events" element={<Navigate to="/app/events" replace />} />
            <Route path="/events/:eventId/guests" element={<EventRedirect subRoute="guests" />} />
            <Route path="/events/:eventId/calendar" element={<EventRedirect subRoute="calendar" />} />
            <Route path="/events/:eventId/schedule" element={<EventRedirect subRoute="schedule" />} />
            <Route path="/events/:eventId/providers" element={<EventRedirect subRoute="providers" />} />
            <Route path="/providers" element={<Navigate to="/app/providers" replace />} />
            <Route path="/account" element={<Navigate to="/app/account" replace />} />
            <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
            <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
